import styled from "@emotion/styled";
import { yupResolver } from "@hookform/resolvers/yup";
import { Delete } from "@mui/icons-material";
import { Alert, IconButton, Stack } from "@mui/material";
import { Button } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { useChurchStore } from "features/churches";
import {
  useChartOfAccounts,
  useCreateExpense,
  useExpenseStore
} from "features/finances";
import { FILTER_OPERATIONS } from "features/members";
import { useTeams } from "features/teams";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useForm } from "react-hook-form";
import { convertObjectToFormData } from "utils/convertObjectToFormData";
import * as yup from "yup";
import { BaseModal } from "./BaseModal";

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  color: #5b5b5b;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
`;

const Select = styled.select`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
  font-family: inherit;
  background-color: transparent;
`;

const Filename = styled.span`
  font-size: 10px;
`;

const DropzoneContainer = styled.div`
  border: 1px dashed #b3b3b3;
  border-radius: 4px;
  padding: 20px;
  text-align: center;
  cursor: pointer;

  &:hover {
    border-color: #3dadb7;
  }
`;

const DropzoneText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #5b5b5b;
`;

const DropzoneSubText = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  color: #b3b3b3;
`;

const expenseSchema = yup.object({
  requisition_number: yup.string(),
  requested_by: yup.string(),
  reason: yup.string(),
  description: yup.string(),
  amount: yup.number().typeError("Amount must be a number"),
  check_number: yup.string(),
  chart_of_account_id: yup.string(),
  verified_by: yup.string(),
  expense_type_id: yup.string(),
  team_id: yup.string(),
  request_date: yup.string(),
  expense_date: yup.string(),
  transaction_number: yup.string()
});

export function RecordExpenseModal() {
  const recordExpenseModalOpen = useExpenseStore(
    state => state.recordExpenseModalOpen
  );
  const closeRecordExpenseModal = useExpenseStore(
    state => state.closeRecordExpenseModal
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const chartOfAccountsQuery = useChartOfAccounts({
    churchId: selectedChurch?.id,
    filters: [
      {
        column: "account_type",
        operator: FILTER_OPERATIONS.EQUAL,
        value: "EXPENSE"
      }
    ]
  });
  const teamsQuery = useTeams({
    churchId: selectedChurch?.id
  });
  const createExpenseMutation = useCreateExpense();
  const [selectedFile, setSelectedFile] = useState(null);

  const onDrop = useCallback(acceptedFiles => {
    setSelectedFile(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "application/pdf": [".pdf"]
    },
    maxSize: 2 * 1024 * 1024, // 2 MB
    multiple: false
  });

  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      requisition_number: "",
      requested_by: "",
      reason: "",
      description: "",
      amount: 0,
      check_number: "",
      chart_of_account_id: "",
      verified_by: "",
      expense_type_id: "",
      team_id: "",
      request_date: "",
      expense_date: "",
      transaction_number: ""
    },
    resolver: yupResolver(expenseSchema)
  });

  const onSubmitAndNew = values => {
    const expenseData = {
      requisition_number: values.requisition_number,
      requested_by: values.requested_by,
      reason: values.reason,
      description: values.description,
      amount: values.amount,
      check_number: values.check_number,
      chart_of_account_id: values.chart_of_account_id,
      verified_by: values.verified_by,
      expense_type_id: values.expense_type_id,
      team_id: values.team_id,
      request_date: values.request_date,
      expense_date: values.expense_date,
      transaction_number: values.transaction_number,
      recorded_date_time: new Date().toISOString()
    };

    if (selectedFile) {
      expenseData["attachment_request"] = selectedFile;
    }

    const expenseFormData = convertObjectToFormData(expenseData);

    createExpenseMutation.mutate(
      {
        churchId: selectedChurch?.id,
        expenseData: expenseFormData
      },
      {
        onSuccess: () => {
          reset();
          setSelectedFile(null);
        }
      }
    );
  };

  const onSubmitAndExit = values => {
    const expenseData = {
      requisition_number: values.requisition_number,
      requested_by: values.requested_by,
      reason: values.reason,
      description: values.description,
      amount: values.amount,
      check_number: values.check_number,
      chart_of_account_id: values.chart_of_account_id,
      verified_by: values.verified_by,
      expense_type_id: values.expense_type_id,
      team_id: values.team_id,
      request_date: values.request_date,
      expense_date: values.expense_date,
      transaction_number: values.transaction_number,
      recorded_date_time: new Date().toISOString()
    };

    if (selectedFile) {
      expenseData["attachment_request"] = selectedFile;
    }

    const expenseFormData = convertObjectToFormData(expenseData);

    createExpenseMutation.mutate(
      {
        churchId: selectedChurch?.id,
        expenseData: expenseFormData
      },
      {
        onSuccess: () => {
          closeRecordExpenseModal();
        }
      }
    );
  };

  return (
    <BaseModal
      title="Record Expense"
      isOpen={recordExpenseModalOpen}
      onClose={closeRecordExpenseModal}
      footer={
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Button
            size="small"
            onClick={handleSubmit(onSubmitAndNew)}
            disabled={createExpenseMutation.isLoading}
          >
            Save and New
          </Button>
          <Button
            size="small"
            onClick={handleSubmit(onSubmitAndExit)}
            disabled={createExpenseMutation.isLoading}
          >
            Save and Exit
          </Button>
        </Stack>
      }
    >
      <Field>
        <Label htmlFor="requisition_number">Requisition ID</Label>
        <Input id="requisition_number" {...register("requisition_number")} />
      </Field>
      <Field>
        <Label htmlFor="requested_by">Requested by</Label>
        <Input id="requested_by" {...register("requested_by")} />
      </Field>
      <Field>
        <Label htmlFor="chart_of_account_id">Category</Label>
        <Select id="chart_of_account_id" {...register("chart_of_account_id")}>
          {chartOfAccountsQuery.data?.result?.map(chartOfAccount => (
            <option key={chartOfAccount.id} value={chartOfAccount.id}>
              {chartOfAccount.account_name}
            </option>
          ))}
        </Select>
      </Field>
      <Stack direction="row" spacing={2}>
        <Field>
          <Label htmlFor="amount">Amount</Label>
          <Input id="amount" type="number" {...register("amount")} />
        </Field>
        <Field>
          <Label htmlFor="currency">Currency</Label>
          <Select id="currency">
            <option>ETB</option>
          </Select>
        </Field>
      </Stack>
      <Field>
        <Label htmlFor="check_number">Cheque Number</Label>
        <Input id="check_number" {...register("check_number")} />
      </Field>
      <Field>
        <Label htmlFor="description">Description</Label>
        <Input id="description" {...register("description")} />
      </Field>
      <Field>
        <Label htmlFor="team_id">Team</Label>
        <Select id="team_id" {...register("team_id")}>
          {teamsQuery.data?.result?.map(team => (
            <option key={team.id} value={team.id}>
              {team.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field>
        <Label htmlFor="verified_by">Verified by</Label>
        <Input id="verified_by" {...register("verified_by")} />
      </Field>
      <Field>
        <Label>Attach request</Label>
        <DropzoneContainer {...getRootProps()}>
          <input {...getInputProps()} />
          <DropzoneText>Click to upload or drag and drop</DropzoneText>
          <DropzoneSubText>PNG, JPG or PDF (max. 2 MB)</DropzoneSubText>
        </DropzoneContainer>
        {selectedFile && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
            <Filename>{selectedFile.name}</Filename>
            <IconButton size="small" onClick={() => setSelectedFile(null)}>
              <Delete fontSize="small" />
            </IconButton>
          </Stack>
        )}

        {fileRejections.length > 0 && (
          <Alert severity="error">
            {fileRejections
              .map(({ errors }) =>
                errors.map(error => {
                  if (error.code === "file-too-large") {
                    return "File size is too large. Maximum size is 2 MB.";
                  }
                  if (error.code === "file-invalid-type") {
                    return "Invalid file type. Please upload PNG, JPG or PDF files only.";
                  }
                  return error.message;
                })
              )
              .join(", ")}
          </Alert>
        )}
      </Field>
      <Field>
        <Label htmlFor="request_date">Request date</Label>
        <Input id="request_date" type="date" {...register("request_date")} />
      </Field>
      <Field>
        <Label htmlFor="expense_date">Expense date</Label>
        <Input id="expense_date" type="date" {...register("expense_date")} />
      </Field>

      <MutationError error={createExpenseMutation.error} sx={{ mt: 2 }} />
    </BaseModal>
  );
}
